import type { ApiClient } from './api/client.ts'

import { ROUTER_SCOPE } from './router-scope.ts'

export type VersionApi = Pick<ApiClient, 'getVersion'>

export const CHECK_EVERY_MS = 60_000

const inApp = (link: HTMLAnchorElement, scope: RegExp): boolean =>
  link.origin === globalThis.location.origin &&
  !link.getAttribute('href')?.startsWith('#') &&
  link.download === '' &&
  (link.target === '' || link.target === '_self') &&
  scope.test(link.pathname)

export const hardenNavigation = (
  go: (href: string) => void = (href) => globalThis.location.replace(href),
  scope: RegExp = ROUTER_SCOPE,
): (() => void) => {
  const pending = new Set<ReturnType<typeof setTimeout>>()

  const onClick = (event: MouseEvent) => {
    if (event.button !== 0 || event.ctrlKey || event.metaKey || event.altKey || event.shiftKey) return

    const link = event
      .composedPath()
      .find((step): step is HTMLAnchorElement => step instanceof HTMLAnchorElement)
    if (link === undefined || !inApp(link, scope)) return

    const { href } = link
    const from = globalThis.location.href

    const settle = setTimeout(() => {
      pending.delete(settle)
      const now = globalThis.location.href
      if (now === href && now !== from) go(href)
    }, 0)

    pending.add(settle)
  }

  globalThis.addEventListener('click', onClick, { capture: true })

  return () => {
    globalThis.removeEventListener('click', onClick, { capture: true })
    for (const settle of pending) clearTimeout(settle)
    pending.clear()
  }
}

export const watchForNewVersion = (
  api: VersionApi,
  onNewVersion: () => void,
  {
    now = () => Date.now(),
    listen = (name: string, handler: () => void) => {
      globalThis.addEventListener(name, handler)
      return () => globalThis.removeEventListener(name, handler)
    },
    hidden = () => globalThis.document?.visibilityState === 'hidden',
  } = {},
): (() => void) => {
  let loaded: string | undefined
  let lastAsked = Number.NEGATIVE_INFINITY
  let stopped = false

  const check = async () => {
    if (stopped || hidden()) return
    if (now() - lastAsked < CHECK_EVERY_MS) return

    lastAsked = now()

    let build: string
    try {
      build = (await api.getVersion()).build_sha
    } catch {
      return
    }

    if (stopped) return
    if (loaded === undefined) {
      loaded = build
      return
    }

    if (build !== loaded) onNewVersion()
  }

  void check()

  const timer = setInterval(() => void check(), CHECK_EVERY_MS)
  const unlisten = [listen('visibilitychange', () => void check()), listen('focus', () => void check())]

  return () => {
    stopped = true
    clearInterval(timer)
    for (const off of unlisten) off()
  }
}
